import type { SearchSource } from "@/analytics/surfaces";
import { GLOBAL_SEARCH_MIN_QUERY_LENGTH, useGlobalSearch } from "@/api/search";
import { useWorkspaces } from "@/api/workspaces";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { ROUTES } from "@/constants/routes";
import { useSidebarContext } from "@/context/SidebarContext";
import type { DraggableData } from "@/dnd/ids";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { RailRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRow";
import { buildCanvasLinkEntity } from "@/pages/canvas/utils/canvasLinkEntity";
import { useOpenFileRecord } from "@/panes/file-record-detail/useOpenFileRecord";
import { cn } from "@/utils";
import {
  trackSearchClosed,
  trackSearchOpened,
  trackSearchPerformed,
  trackSearchResultOpened,
} from "@/utils/analytics";
import { route } from "@/utils/route";
import type {
  FileRecordClass,
  SearchGroup,
  SearchHit,
  SearchResourceType,
  SearchResponse,
} from "@cohesive/api-client";
import { FileRecordTypeIcon } from "@cohesive/ui-components";
import { useDndMonitor, useDraggable } from "@dnd-kit/core";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Frame,
  Layers,
  MessagesSquare,
  Search,
  X,
  type LucideIcon,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Fired by the layout-level ⌘K/Ctrl+K hotkey (see WorkspaceRailSearchHotkey).
 * A DOM event rather than context state so the hotkey can live outside the
 * rail — the rail (and this component) unmounts when collapsed, but the hotkey
 * must keep working and re-open it first.
 */
export const OPEN_GLOBAL_SEARCH_EVENT = "cohesive:open-global-search";

const GROUP_ORDER: {
  type: SearchResourceType;
  label: string;
  groupKey: keyof Pick<
    SearchResponse,
    "workspaces" | "canvases" | "chats" | "files"
  >;
}[] = [
  { type: "workspace", label: "Workspaces", groupKey: "workspaces" },
  { type: "canvas", label: "Canvases", groupKey: "canvases" },
  { type: "chat", label: "Chats", groupKey: "chats" },
  { type: "file", label: "Files", groupKey: "files" },
];

// Same icons the rail rows use for each resource, so a search hit reads as the
// thing it navigates to. Files override with FileRecordTypeIcon (see row).
const TYPE_ICONS: Record<SearchResourceType, LucideIcon> = {
  workspace: Layers,
  canvas: Frame,
  chat: MessagesSquare,
  file: FileText,
};

const EXPANDED_LIMIT = 20;
// Breadcrumbs always show up to two levels; deeper trails drop leading
// segments behind an ellipsis, and in-line overflow is CSS-truncated.
const BREADCRUMB_MAX_SEGMENTS = 2;

/**
 * Global search, pinned above the rail content. One real input: typing drops a
 * results-only panel anchored beneath it (no second input inside the panel).
 * Results render as RailRow — the same leaf rows the rail itself uses.
 */
export function WorkspaceRailSearch({
  onActiveChange,
}: {
  /** Reports `focused || has text` — the head yields the row to the field
   *  while the search is active (the create glyph folds away). */
  onActiveChange?: (active: boolean) => void;
} = {}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [typeFilter, setTypeFilter] = useState<SearchResourceType | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  // Blur-with-text keeps the field expanded: a live filter must show itself
  // and its escape hatch.
  const searchActive = focused || query.length > 0;
  useEffect(() => {
    onActiveChange?.(searchActive);
  }, [searchActive, onActiveChange]);

  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  // The hotkey marks the next open; any field-driven open consumes/resets it.
  const openSourceRef = useRef<SearchSource>("rail_field");
  const selectedResultRef = useRef(false);
  const lastTrackedQueryRef = useRef<string | null>(null);

  // Never tracks inside the updater (StrictMode runs updaters twice).
  const openPanel = () => {
    if (!panelOpen) {
      trackSearchOpened({ source: openSourceRef.current });
      openSourceRef.current = "rail_field";
    }
    setPanelOpen(true);
  };

  const trimmed = query.trim();

  const { data, isFetching, isDebouncing } = useGlobalSearch(query, {
    types: typeFilter ? [typeFilter] : undefined,
    limit: typeFilter ? EXPANDED_LIMIT : undefined,
    enabled: panelOpen,
  });
  // The debounce gap counts as in-flight: `data` still describes the previous
  // query then, so deciding "No results." on it would flash a false empty
  // state on every fresh keystroke.
  const pending = isFetching || isDebouncing;

  const navigate = useNavigate();
  const openFileRecord = useOpenFileRecord();

  // The private workspace's stored row name is "Private Workspace"; the whole
  // UI renders it as "My Workspace". Inferred from the rail's cached workspace
  // list rather than extra fields on the search response.
  const { data: workspaces } = useWorkspaces();
  const privateWorkspaceId = useMemo(
    () => workspaces?.find((ws) => ws.is_private)?.id,
    [workspaces],
  );

  const close = useCallback(() => {
    if (panelOpen) {
      trackSearchClosed({
        hadQuery: query.length > 0,
        selectedResult: selectedResultRef.current,
      });
    }
    selectedResultRef.current = false;
    lastTrackedQueryRef.current = null;
    setPanelOpen(false);
    setQuery("");
    setTypeFilter(null);
    setHighlightIndex(0);
  }, [panelOpen, query]);

  // The layout-level hotkey asks us to focus via a DOM event (see
  // OPEN_GLOBAL_SEARCH_EVENT for why it isn't plain context state).
  useEffect(() => {
    const handleOpen = () => {
      openSourceRef.current = "keyboard_shortcut";
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener(OPEN_GLOBAL_SEARCH_EVENT, handleOpen);
    return () =>
      window.removeEventListener(OPEN_GLOBAL_SEARCH_EVENT, handleOpen);
  }, []);

  // Track drags that start on our rows: the panel must survive the gesture
  // (Radix would otherwise dismiss on outside interaction), then close on drop
  // so the target (canvas board, chat composer) is unobstructed afterwards.
  const draggingRef = useRef(false);
  useDndMonitor({
    onDragStart: (event) => {
      if (String(event.active.id).startsWith("global-search-result:")) {
        draggingRef.current = true;
        const [, type, id] = String(event.active.id).split(":");
        trackSearchResultOpened({
          resultType: type,
          position: flatHits.findIndex((h) => h.type === type && h.id === id),
          method: "drag",
        });
        selectedResultRef.current = true;
      }
    },
    onDragEnd: (event) => {
      const wasOurs = draggingRef.current;
      draggingRef.current = false;
      if (
        wasOurs &&
        String(event.active.id).startsWith("global-search-result:")
      ) {
        close();
      }
    },
    onDragCancel: () => {
      draggingRef.current = false;
    },
  });

  const handleHoverIndex = useCallback((index: number) => {
    setHighlightIndex(index);
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    return GROUP_ORDER.filter((g) => !typeFilter || g.type === typeFilter).map(
      (g) => ({ ...g, group: data[g.groupKey] as SearchGroup }),
    );
  }, [data, typeFilter]);

  const flatHits = useMemo(
    () => groups.flatMap((g) => g.group.items),
    [groups],
  );

  const openHit = useCallback(
    (hit: SearchHit, method: "click" | "keyboard" = "click") => {
      trackSearchResultOpened({
        resultType: hit.type,
        position: flatHits.indexOf(hit),
        method,
      });
      selectedResultRef.current = true;
      switch (hit.type) {
        case "workspace":
          navigate(route(ROUTES.WORKSPACES.DETAIL, { workspaceId: hit.id }));
          break;
        case "canvas":
          navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: hit.id }));
          break;
        case "chat":
          navigate(route(ROUTES.CHAT.DETAIL, { flowSessionId: hit.id }));
          break;
        case "file":
          openFileRecord(hit.id, {
            flowSessionId: hit.flow_session_id,
            analyticsSource: "rail_search",
          });
          break;
      }
      close();
    },
    [navigate, openFileRecord, close, flatHits],
  );

  // One Search Performed per settled query (deduped; the response echoes the
  // query it answered, so refinements and the type-filter requery don't
  // re-emit). Raw query kept by design.
  useEffect(() => {
    if (!panelOpen || pending || !data) return;
    if (data.query.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) return;
    if (lastTrackedQueryRef.current === data.query) return;
    lastTrackedQueryRef.current = data.query;
    const allGroups = [data.workspaces, data.canvases, data.chats, data.files];
    trackSearchPerformed({
      query: data.query,
      resultCount: allGroups.reduce((n, g) => n + g.total_count, 0),
      resultTypes: GROUP_ORDER.filter(
        (g) => data[g.groupKey].items.length > 0,
      ).map((g) => g.type),
    });
  }, [panelOpen, pending, data]);

  // Keep the keyboard highlight on a real row as results change.
  useEffect(() => {
    setHighlightIndex((current) =>
      current < 0
        ? -1
        : flatHits.length === 0
          ? 0
          : Math.min(current, flatHits.length - 1),
    );
  }, [flatHits.length]);

  const hasAnyResults = flatHits.length > 0;
  const belowMinLength = trimmed.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH;

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      inputRef.current?.blur();
      return;
    }
    if (!panelOpen) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (flatHits.length === 0) return;
      const delta = e.key === "ArrowDown" ? 1 : -1;
      setHighlightIndex((current) => {
        // From the cleared state (utility-row hover), enter at the list edge.
        if (current < 0) return delta === 1 ? 0 : flatHits.length - 1;
        return (current + delta + flatHits.length) % flatHits.length;
      });
      return;
    }
    if (e.key === "Enter") {
      const hit = flatHits[highlightIndex];
      if (hit) {
        e.preventDefault();
        openHit(hit, "keyboard");
      }
    }
  };

  return (
    <Popover
      open={panelOpen}
      onOpenChange={(next) => {
        if (!next && draggingRef.current) return; // keep the panel up mid-drag
        if (!next) close();
      }}
      modal={false}
    >
      <PopoverAnchor asChild>
        <div
          ref={fieldRef}
          className="flex items-center gap-2 rounded-md border border-n-10 px-2 focus-within:border-n-20"
        >
          <Search className="size-4 shrink-0 text-n-40" />
          <input
            ref={inputRef}
            type="text"
            aria-label="Search everything"
            placeholder="Search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlightIndex(0);
              openPanel();
            }}
            onFocus={() => {
              setFocused(true);
              if (trimmed.length > 0) openPanel();
            }}
            onBlur={() => {
              setFocused(false);
              openSourceRef.current = "rail_field";
            }}
            onKeyDown={handleInputKeyDown}
            className={cn(
              "min-h-8 w-full select-text bg-transparent text-base text-n-80 outline-none",
              "placeholder:text-n-40",
            )}
          />
          {query ? (
            <button
              type="button"
              onClick={() => {
                close();
                inputRef.current?.focus();
              }}
              className="shrink-0 rounded-md p-1 text-n-40 hover:bg-n-05 hover:text-n-70"
              aria-label="Clear search"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={6}
        data-testid="global-search-results"
        className="w-[min(400px,90vw)] overflow-hidden rounded-lg border border-n-10 bg-popover p-0 shadow-elev-3"
        // The input in the rail stays the one and only focus owner.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Clicking back into the anchored input is not "outside".
          if (fieldRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
      >
        <div className="max-h-[min(60vh,480px)] overflow-y-auto p-1">
          {belowMinLength && !isDebouncing ? (
            <div className="px-3 py-5 text-center text-sm text-n-40">
              Type at least {GLOBAL_SEARCH_MIN_QUERY_LENGTH} characters to
              search.
            </div>
          ) : !hasAnyResults && pending ? (
            <div className="px-3 py-5 text-center text-sm text-n-40">
              Searching…
            </div>
          ) : !hasAnyResults ? (
            <div className="px-3 py-5 text-center text-sm text-n-40">
              No results.
            </div>
          ) : (
            // hasAnyResults while pending intentionally keeps the previous
            // query's hits on screen (keepPreviousData) — no flicker while
            // typing; the list swaps when the new response lands.
            <>
              {typeFilter ? (
                <RailRow
                  icon={ArrowLeft}
                  label="All results"
                  className="cursor-pointer text-n-40"
                  onMouseEnter={() => setHighlightIndex(-1)}
                  onClick={() => {
                    setTypeFilter(null);
                    setHighlightIndex(0);
                    inputRef.current?.focus();
                  }}
                />
              ) : null}
              {groups.map(({ type, label, group }) => {
                if (group.items.length === 0) return null;
                const groupStart = flatHits.indexOf(group.items[0]);
                return (
                  <div key={type}>
                    <div className="px-2 pb-1 pt-2 text-xs font-medium text-n-40">
                      {label} · {group.total_count}
                    </div>
                    {group.items.map((hit, index) => (
                      <SearchResultRow
                        key={`${hit.type}:${hit.id}`}
                        hit={hit}
                        index={groupStart + index}
                        highlighted={groupStart + index === highlightIndex}
                        onOpen={openHit}
                        onHoverIndex={handleHoverIndex}
                        privateWorkspaceId={privateWorkspaceId}
                      />
                    ))}
                    {!typeFilter && group.has_more ? (
                      <RailRow
                        icon={ArrowRight}
                        label={`Show more ${label.toLowerCase()}`}
                        className="cursor-pointer text-n-40"
                        onMouseEnter={() => setHighlightIndex(-1)}
                        onClick={() => {
                          setTypeFilter(type);
                          setHighlightIndex(0);
                          inputRef.current?.focus();
                        }}
                      />
                    ) : null}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const SearchResultRow = memo(function SearchResultRow({
  hit,
  index,
  highlighted,
  onOpen,
  onHoverIndex,
  privateWorkspaceId,
}: {
  hit: SearchHit;
  index: number;
  highlighted: boolean;
  onOpen: (hit: SearchHit) => void;
  /** Mouse and keyboard share one highlight: hovering moves it here, so a
   *  single row is ever active and Enter always opens what's visibly lit. */
  onHoverIndex: (index: number) => void;
  privateWorkspaceId: string | undefined;
}) {
  const draggableData = useMemo(() => buildSearchHitDragData(hit), [hit]);

  const draggable = useDraggable({
    id: `global-search-result:${hit.type}:${hit.id}`,
    data: draggableData ?? undefined,
    disabled: !draggableData,
  });

  const rowRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (highlighted) {
      rowRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [highlighted]);

  const breadcrumb = breadcrumbFor(hit, privateWorkspaceId);

  return (
    <RailRow
      ref={(node) => {
        rowRef.current = node;
        draggable.setNodeRef(node);
      }}
      data-testid="global-search-result"
      className="cursor-pointer"
      icon={TYPE_ICONS[hit.type]}
      leading={
        hit.type === "file" ? (
          <FileRecordTypeIcon
            contentType={hit.content_type ?? ""}
            className={cn("size-4", breadcrumb && "mt-[3px]")}
          />
        ) : undefined
      }
      label={
        hit.type === "workspace" && hit.id === privateWorkspaceId
          ? "My Workspace"
          : hit.name
      }
      subLabel={breadcrumb || undefined}
      active={highlighted}
      onClick={() => onOpen(hit)}
      onMouseEnter={() => onHoverIndex(index)}
      {...(draggableData ? draggable.listeners : {})}
      {...(draggableData ? draggable.attributes : {})}
    />
  );
});

/**
 * Location line under a hit: "Workspace › Container" — a purely visual cue
 * (not navigable). Always shows up to two levels; deeper trails drop leading
 * segments behind an ellipsis (the nearest container is the distinguishing
 * half), and in-line overflow is CSS-truncated at the tail. The private
 * workspace renders under its display alias, matching the rest of the UI.
 */
function breadcrumbFor(
  hit: SearchHit,
  privateWorkspaceId: string | undefined,
): string {
  const workspaceName = hit.workspace_name
    ? hit.workspace_id === privateWorkspaceId
      ? "My Workspace"
      : hit.workspace_name
    : undefined;
  let segments = [workspaceName, hit.canvas_name, hit.flow_session_name].filter(
    (s): s is string => Boolean(s),
  );
  if (segments.length === 0) return "";

  const elided = segments.length > BREADCRUMB_MAX_SEGMENTS;
  if (elided) {
    segments = segments.slice(segments.length - BREADCRUMB_MAX_SEGMENTS);
  }
  return (elided ? "… › " : "") + segments.join(" › ");
}

/**
 * Maps a search hit onto the app-wide dnd payloads so results drop anywhere
 * rail rows already do (canvas board mints nodes / copies files, chat composer
 * attaches). Workspaces and folders aren't draggable.
 */
function buildSearchHitDragData(hit: SearchHit): DraggableData | null {
  switch (hit.type) {
    case "file":
      return {
        kind: "files",
        payloads: [
          {
            recordId: hit.id,
            sourceClass: (hit.class ?? "workspace") as FileRecordClass,
            sourceCanvasId: hit.canvas_id,
            sourceWorkspaceId: hit.workspace_id,
            sourceFlowSessionId: hit.flow_session_id,
            sourcePath: hit.path ?? hit.name,
            isFolder: false,
            contentType: hit.content_type ?? "",
            name: hit.name,
          },
        ],
        primaryName: hit.name,
      };
    case "canvas":
      return {
        kind: "canvas-card",
        canvasIds: [hit.id],
        sourceWorkspaceId: hit.workspace_id ?? "",
        // A search hit is not a tree row, so its folder is unknown — a drop
        // runs the move, which is idempotent if it lands where it already is.
        primaryName: hit.name,
        entities: [
          buildCanvasLinkEntity({
            id: hit.id,
            name: hit.name,
            workspace_id: hit.workspace_id,
          }),
        ],
      };
    // Chats aren't draggable: there is nowhere to drop one (no folders, no
    // cross-workspace moves) — they open, pin, rename, archive from menus.
    default:
      return null;
  }
}

/**
 * Layout-level ⌘K/Ctrl+K hotkey. Lives outside the rail so the shortcut keeps
 * working while the rail is collapsed: it re-opens the rail, then focuses the
 * (freshly mounted) WorkspaceRailSearch input via a DOM event.
 */
export function WorkspaceRailSearchHotkey() {
  const { railOpen, setRailOpen, isDrawerOpen, setIsDrawerOpen } =
    useSidebarContext();
  const lg = useBreakpoint("lg");

  // The handler reads live state through a ref so the listener never re-binds.
  const stateRef = useRef({ railOpen, isDrawerOpen, lg });
  stateRef.current = { railOpen, isDrawerOpen, lg };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "k" || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();

      // Below lg the rail renders as a drawer — `railOpen` is irrelevant there
      // and the search field only mounts while the drawer is open.
      const {
        railOpen: rail,
        isDrawerOpen: drawer,
        lg: isLg,
      } = stateRef.current;
      const searchMounted = isLg ? rail : drawer;
      if (!searchMounted) {
        if (isLg) setRailOpen(true);
        else setIsDrawerOpen(true);
        // Give the container a frame to mount before signalling the field.
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_SEARCH_EVENT));
        }, 50);
        return;
      }

      window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_SEARCH_EVENT));
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setRailOpen, setIsDrawerOpen]);

  return null;
}
